import { Link } from "@tanstack/react-router";
import { validFaqItems } from "@/lib/faq-schema";
import { ES_BY_EN } from "@/lib/es-pages";
import { schemaDateFieldsFromUrl } from "@/lib/schema-dates";

export const CURRENT_AS_OF = "July 30, 2026";
export const SITE = "https://virginiabariatric.com";

export function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-5 rounded-2xl border border-brand/30 bg-brand-soft/50 p-5 text-foreground/85 leading-[1.6]">
      {children}
    </div>
  );
}

export function H2({ children, id }: { children: React.ReactNode; id?: string }) {
  return (
    <h2
      id={id}
      className="mt-8 scroll-mt-24 text-2xl md:text-3xl font-serif text-brand-mid"
    >
      {children}
    </h2>
  );
}

export function H3({ children }: { children: React.ReactNode }) {
  return <h3 className="mt-6 text-xl font-serif text-brand-mid">{children}</h3>;
}

export function H4({ children }: { children: React.ReactNode }) {
  return <h4 className="mt-5 text-lg font-serif text-brand-mid">{children}</h4>;
}


export function P({ children }: { children: React.ReactNode }) {
  return <p className="mt-3 text-foreground/85 leading-[1.6]">{children}</p>;
}

export function UL({ children }: { children: React.ReactNode }) {
  return (
    <ul className="mt-3 list-disc space-y-2 pl-6 text-foreground/85 leading-[1.6]">
      {children}
    </ul>
  );
}

export type Faq = { q: string; a: React.ReactNode; schemaAnswer?: string };

export function FaqList({ faqs }: { faqs: Faq[] }) {
  return (
    <div className="mt-5 space-y-4">
      {faqs.map((f) => (
        <div
          key={f.q}
          id={faqAnchorId(f.q)}
          className="scroll-mt-24 rounded-2xl border border-border bg-card p-5"
        >
          <h3 className="text-lg font-serif text-brand-mid">{f.q}</h3>
          <p className="mt-2 text-foreground/85 leading-[1.6]">{f.a}</p>
        </div>
      ))}
    </div>
  );
}

/** Slugify a question into a stable in-page anchor id. */
export function faqAnchorId(question: string): string {
  return question
    .normalize("NFD")
    // Strip combining accents so Spanish questions slugify to readable anchors.
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);
}

function faqAnswerText(f: Faq): string {
  if (f.schemaAnswer) return f.schemaAnswer;
  return typeof f.a === "string" ? f.a : "";
}

export function faqSchema(faqs: Faq[], url?: string, lang = "en-US") {
  const valid = validFaqItems(
    (faqs ?? []).map((f) => ({ question: f?.q, answer: faqAnswerText(f) })),
  );

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: lang,
    ...(url
      ? {
          "@id": `${url}#faq`,
          url,
          isPartOf: { "@id": `${url}#webpage` },
          mainEntityOfPage: { "@id": `${url}#webpage` },
          publisher: { "@id": `${SITE}/#organization` },
        }
      : {}),
    mainEntity: valid.map((f) => {
      const anchor = url ? `${url}#${faqAnchorId(f.question)}` : undefined;
      return {
        "@type": "Question",
        ...(anchor ? { "@id": anchor, url: anchor } : {}),
        name: f.question,
        inLanguage: lang,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.answer,
          ...(anchor ? { url: anchor } : {}),
        },
      };
    }),
  };
}


export type PageImage = {
  url: string;
  alt: string;
  width?: number;
  height?: number;
};

export function pageImageSchema(url: string, image: PageImage) {
  return {
    "@type": "ImageObject",
    "@id": `${url}#primaryimage`,
    url: image.url,
    contentUrl: image.url,
    caption: image.alt,
    ...(image.width ? { width: image.width } : {}),
    ...(image.height ? { height: image.height } : {}),
  };
}

export function medicalWebPageSchema({
  url,
  name,
  description,
  image,
  hasFaq,
  audienceType = "Patients seeking weight-loss medication coverage",
  lastReviewed = "2026-08-09",
}: {
  url: string;
  name: string;
  description: string;
  /** Primary image of the page; also used as the OpenGraph preview. */
  image?: PageImage;
  /** Link the page to its FAQPage node (`${url}#faq`). */
  hasFaq?: boolean;
  audienceType?: string;
  lastReviewed?: string;
}) {
  // Spanish twin (if any) from the same map that drives hreflang, so the
  // markup states the same pairing as the link annotations.
  const spanishPath = ES_BY_EN[new URL(url).pathname];

  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    ...(spanishPath ? { workTranslation: { "@id": `${SITE}${spanishPath}#webpage` } } : {}),
    ...schemaDateFieldsFromUrl(url, lastReviewed),
    datePublished: "2026-01-15",
    "@id": `${url}#webpage`,
    url,
    name,
    description,
    inLanguage: "en-US",
    isPartOf: { "@id": `${SITE}/#website` },
    breadcrumb: { "@id": `${url}#breadcrumb` },
    publisher: { "@id": `${SITE}/#organization` },
    about: { "@type": "MedicalCondition", name: "Obesity" },
    audience: {
      "@type": "MedicalAudience",
      audienceType,
      geographicArea: { "@type": "AdministrativeArea", name: "Virginia" },
    },
    ...(image
      ? {
          primaryImageOfPage: pageImageSchema(url, image),
          image: { "@id": `${url}#primaryimage` },
        }
      : {}),
    ...(hasFaq
      ? {
          mainEntity: { "@id": `${url}#faq` },
          significantLink: `${url}#faq`,
        }
      : {}),
    // MedicalSpecialty is an enumeration: "Bariatric" is not a member, so
    // validators flag it. Surgical is the closest valid enum value.
    specialty: "https://schema.org/Surgical",
    // Reference the Physician node in the site-wide @graph instead of
    // re-declaring a thin duplicate Person.
    reviewedBy: { "@id": `${SITE}/#physician-fitzer` },
    mainEntityOfPage: { "@id": `${url}#webpage` },
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "h2"],
    },
  };
}



export const SPOKES = [
  {
    to: "/medical-weight-loss/insurance/medicaid-cova" as const,
    label: "Virginia Medicaid & state employees",
    blurb:
      "Cardinal Care&rsquo;s strict BMI rules, and the July 2026 COVA change to a BMI of 35.",
  },
  {
    to: "/medical-weight-loss/insurance/medicare-tricare-fehb" as const,
    label: "Medicare, TRICARE & federal employees",
    blurb:
      "The $50 Medicare GLP-1 Bridge, TRICARE step therapy, and FEHB requirements.",
  },
  {
    to: "/medical-weight-loss/insurance/commercial-marketplace" as const,
    label: "Employer, commercial & marketplace plans",
    blurb:
      "Sentara, UnitedHealthcare, Cigna, Anthem, Aetna, your PBM, and why marketplace plans exclude these drugs.",
  },
  {
    to: "/medical-weight-loss/insurance/cash-pay-denials" as const,
    label: "Denials & cash-pay pricing",
    blurb:
      "What to do after a denial, current LillyDirect and NovoCare self-pay prices, and questions to ask your plan.",
  },
];

export function SpokeCards({ exclude }: { exclude?: string }) {
  const items = SPOKES.filter((s) => s.to !== exclude);
  return (
    <div className="mt-5 grid gap-4 sm:grid-cols-2">
      {items.map((s) => (
        <Link
          key={s.to}
          to={s.to}
          className="block rounded-2xl border border-border bg-card p-5 transition hover:border-brand/50"
        >
          <span className="block text-lg font-serif text-brand-mid">{s.label}</span>
          <span
            className="mt-2 block text-sm text-foreground/75 leading-[1.6]"
            dangerouslySetInnerHTML={{ __html: s.blurb }}
          />
        </Link>
      ))}
    </div>
  );
}

export function InsuranceCTA() {
  return (
    <section className="py-8 bg-brand-soft/30">
      <div className="mx-auto max-w-3xl px-4 text-center">
        <h2 className="text-2xl md:text-3xl font-serif">Not sure where you stand?</h2>
        <p className="mt-3 text-foreground/85 leading-[1.6]">
          Our team helps patients verify benefits and submit prior authorizations every day. Start
          with a consultation and we&rsquo;ll work through your plan&rsquo;s requirements together.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/appointments"
            className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Request a consultation
          </Link>
          <Link
            to="/medical-weight-loss/medications"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Weight-loss medications
          </Link>
        </div>
      </div>
    </section>
  );
}

export function CurrencyNote({ currentAsOf = CURRENT_AS_OF }: { currentAsOf?: string }) {
  return (
    <Callout>
      <strong>Information current as of {currentAsOf}.</strong> Coverage rules for these
      medications change frequently &mdash; several major Virginia plans changed their rules on
      July 1, 2026. Always confirm details with your own plan before making decisions.
    </Callout>
  );
}
